import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from '../components/ui/button';

const money = (n, c = 'PEN') => `${c === 'PEN' ? 'S/' : c} ${Number(n || 0).toFixed(2)}`;
const Card = ({ title, children, testid }) => (
  <div className="bg-gray-900 rounded-xl border border-gray-800 p-5" data-testid={testid}>
    <h3 className="text-white font-semibold mb-3">{title}</h3>{children}
  </div>
);

const CreditLookup = () => {
  const [uid, setUid] = useState('');
  const [data, setData] = useState(null);
  const lookup = () => api.get(`/admin/finance/credits/${uid.trim()}`).then((r) => setData(r.data))
    .catch((e) => toast.error(e?.response?.data?.detail || 'No encontrado'));
  return (
    <Card title="Créditos por usuario (solo consulta)" testid="finance-credit-lookup">
      <div className="flex gap-2">
        <input placeholder="ID de usuario" value={uid} onChange={(e) => setUid(e.target.value)} data-testid="finance-credit-uid"
          className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
        <Button onClick={lookup} disabled={!uid.trim()} data-testid="finance-credit-search"><Search className="w-4 h-4" /></Button>
      </div>
      {data && (
        <div className="mt-3 text-sm text-gray-300">
          <p data-testid="finance-credit-balance">Saldo: <strong className="text-white">{data.balance}</strong> créditos</p>
          <ul className="mt-2 max-h-48 overflow-y-auto text-xs text-gray-400 space-y-1">
            {data.transactions.map((t) => <li key={t.id}>{new Date(t.created_at).toLocaleString('es-PE')} · {t.type} {t.delta > 0 ? '+' : ''}{t.delta} → {t.balance_after} · {t.reason}</li>)}
          </ul>
        </div>
      )}
    </Card>
  );
};

export const FinancePanel = () => {
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get('/admin/finance/summary').then((r) => setSummary(r.data)).catch(() => toast.error('No se pudo cargar el resumen'));
    api.get('/admin/finance/payments', { params: { limit: 50 } }).then((r) => setPayments(r.data)).catch(() => {});
    api.get('/admin/plans').then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="finance-panel">
      {summary && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card title={`Pagos (${summary.days} días)`} testid="finance-payments-summary">
            <ul className="text-sm text-gray-300 space-y-1">
              {summary.payments.map((p) => <li key={`${p.payment_type}-${p.status}`}>{p.payment_type} · {p.status}: {p.n} · {money(p.amount)}</li>)}
              {summary.payments.length === 0 && <li className="text-gray-500">Sin pagos</li>}
            </ul>
          </Card>
          <Card title="Suscripciones" testid="finance-subs-summary">
            <ul className="text-sm text-gray-300 space-y-1">
              {summary.subscriptions.map((s) => <li key={`${s.plan_id}-${s.status}`}>{s.plan_id} · {s.status}: {s.n}</li>)}
            </ul>
          </Card>
          <Card title={`Créditos (${summary.days} días)`} testid="finance-credits-summary">
            <p className="text-sm text-gray-300">Comprados: {summary.credits.purchased} · Consumidos: {summary.credits.consumed}</p>
            <p className="text-sm text-gray-300">Reembolsados: {summary.credits.refunded} · Regalados: {summary.credits.granted}</p>
          </Card>
        </div>
      )}
      <CreditLookup />
      <Card title="Planes" testid="finance-plans">
        <ul className="text-sm text-gray-300 space-y-1">
          {plans.map((p) => <li key={p.name}>{p.name} · {money(p.price, p.currency)} / {p.billing_interval} · {p.active ? 'activo' : 'inactivo'} · {p.culqi_plan_id ? 'sincronizado' : 'sin sincronizar'}</li>)}
        </ul>
      </Card>
      <Card title="Últimos pagos" testid="finance-payments-list">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-gray-300">
            <thead className="text-gray-500 uppercase"><tr><th className="text-left py-1">Fecha</th><th className="text-left">Usuario</th><th className="text-left">Tipo</th><th className="text-left">Monto</th><th className="text-left">Estado</th><th className="text-left">Recibo</th></tr></thead>
            <tbody>{payments.map((p) => (
              <tr key={p.id} className="border-t border-gray-800"><td className="py-1">{new Date(p.created_at).toLocaleString('es-PE')}</td><td>{p.email}</td><td>{p.payment_type}</td><td>{money(p.amount, p.currency)}</td><td>{p.status}</td><td>{p.receipt_number || '—'}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </Card>
      <p className="text-xs text-gray-500">Los saldos y pagos solo cambian por el proveedor de pagos y el ledger; no existen ajustes manuales.</p>
    </div>
  );
};

export default FinancePanel;
